import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyInr,
  DeviceMobile,
  MapPin,
  PaperPlaneTilt,
  Path,
  QrCode,
  ShieldCheck,
  Truck,
  Wallet
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PLAY_STORE_URL = "https://play.google.com/store/search?q=Loado&c=apps";
const PLAY_STORE_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=12&data=${encodeURIComponent(PLAY_STORE_URL)}`;

const vehicleCategories = [
  "Two Wheeler",
  "Three Wheeler",
  "Pickup 7ft",
  "Pickup 8ft",
  "Mini Truck",
  "Open Truck",
  "Tipper",
  "Trailer",
  "Other"
];

const benefits = [
  {
    icon: CurrencyInr,
    title: "Earn from site deliveries",
    description: "Accept material movement jobs from construction customers, sellers, and site buyers."
  },
  {
    icon: Clock,
    title: "Flexible work timing",
    description: "Choose delivery opportunities that match your vehicle, route, and available time."
  },
  {
    icon: Path,
    title: "Local city demand",
    description: "Serve nearby construction supply movements with clear pickup and drop details."
  },
  {
    icon: ShieldCheck,
    title: "Verified platform flow",
    description: "Work through an organized platform built for construction logistics and support."
  }
];

const partnerSteps = [
  "Share your driver and vehicle details",
  "Complete verification and onboarding support",
  "Download the Loado app from Play Store",
  "Start receiving eligible delivery opportunities"
];

export default function DeliveryPartner({ onSubmit }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
    vehicle_category: "",
    vehicle_number: "",
    city: "Chennai",
    message: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await onSubmit(formData);
    setIsLoading(false);

    if (result.success) {
      setIsSuccess(true);
      setFormData({
        name: "",
        mobile_number: "",
        vehicle_category: "",
        vehicle_number: "",
        city: "Chennai",
        message: ""
      });
    }
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-[#F8FAFC]">
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <button
            onClick={() => navigate("/")}
            data-testid="delivery-back-to-home"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft size={20} weight="bold" />
            <span className="font-medium">Back to Home</span>
          </button>

          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-5">
                <Truck size={22} weight="duotone" className="text-[#0055FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                  Delivery Partner
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black text-[#0F172A] mb-5 leading-tight">
                Move construction materials with Loado
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
                A Loado delivery partner is a driver, vehicle owner, or fleet
                operator who transports construction materials between sellers,
                stores, project sites, and customers through Loado's logistics flow.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <a
                  href="#delivery-onboard"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0055FF] text-white font-semibold rounded-lg hover:bg-[#0044CC] transition-colors"
                >
                  Apply for Chennai
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="delivery-play-store-link"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
                >
                  Open Play Store
                  <ArrowSquareOut size={20} weight="bold" />
                </a>
              </div>
            </div>

            <div className="bg-[#0F172A] text-white p-6 sm:p-8 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: MapPin, label: "Available city", value: "Chennai" },
                  { icon: Truck, label: "Vehicles", value: "Bike to truck" },
                  { icon: Wallet, label: "For", value: "Drivers & fleets" },
                  { icon: DeviceMobile, label: "App", value: "Play Store" }
                ].map((item) => (
                  <div key={item.label} className="border border-white/10 p-4 rounded-lg bg-white/5">
                    <item.icon size={24} weight="duotone" className="text-[#0055FF] mb-3" />
                    <p className="text-xs text-slate-400">{item.label}</p>
                    <p className="font-bold mt-1">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm text-slate-300 leading-relaxed">
                  Loado delivery partners help move cement, steel, sand,
                  aggregates, blocks, hardware, and other construction materials
                  across active service areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div>
              <h2 className="text-2xl lg:text-3xl font-black text-[#0F172A] mb-4">
                Why join as a delivery partner?
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Loado is focused on construction logistics, where reliable local
                vehicle availability matters every day.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="p-5 bg-white border border-slate-200 rounded-lg">
                  <div className="w-11 h-11 bg-[#0055FF]/10 flex items-center justify-center rounded-lg mb-4">
                    <benefit.icon size={24} weight="duotone" className="text-[#0055FF]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="app-download" className="py-14 lg:py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <QrCode size={20} weight="duotone" className="text-[#FF5500]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF5500]">
                  App Download
                </span>
              </div>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0F172A] mb-4">
                Scan to download the Loado app
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Delivery partners can use the app for onboarding, route updates,
                order handling, and delivery coordination once their account is active.
              </p>
              <div className="space-y-3">
                {partnerSteps.map((step) => (
                  <div key={step} className="flex gap-3">
                    <CheckCircle size={20} weight="fill" className="text-[#0055FF] mt-0.5 shrink-0" />
                    <p className="text-sm font-medium text-slate-700">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#F8FAFC] border border-slate-200 p-6 sm:p-8 rounded-lg">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="bg-white border border-slate-200 p-4 rounded-lg">
                  <img
                    src={PLAY_STORE_QR_URL}
                    alt="QR code to open Loado app on Play Store"
                    className="w-48 h-48"
                    data-testid="delivery-play-store-qr"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500 mb-1">Available on</p>
                  <p className="text-2xl font-black text-[#0F172A] mb-4">Google Play Store</p>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    Download App
                    <ArrowSquareOut size={18} weight="bold" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="delivery-onboard" className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <MapPin size={20} weight="duotone" className="text-[#0055FF]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0055FF]">
                  Chennai onboarding
                </span>
              </div>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0F172A] mb-4">
                Apply for delivery partner support
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Loado delivery partner onboarding is currently available in
                Chennai. Submit your details and our team will help with next
                steps, verification, and app setup.
              </p>
              <div className="p-5 bg-white border border-slate-200 rounded-lg">
                <p className="text-sm font-semibold text-[#0F172A] mb-2">Currently available city</p>
                <p className="inline-flex items-center gap-2 text-[#0055FF] font-bold">
                  <MapPin size={18} weight="fill" />
                  Chennai
                </p>
              </div>
            </div>

            <div className="bg-white p-6 sm:p-8 lg:p-10 border border-slate-200 rounded-lg">
              {isSuccess ? (
                <div data-testid="delivery-support-success" className="text-center py-12">
                  <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Request Submitted</h3>
                  <p className="text-slate-600 mb-6">
                    Our delivery partner support team will contact you shortly.
                  </p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="text-[#0055FF] font-medium hover:underline"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="delivery_name" className="text-sm font-medium text-slate-700">
                        Name *
                      </Label>
                      <Input
                        id="delivery_name"
                        data-testid="delivery-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Driver Name"
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="delivery_mobile" className="text-sm font-medium text-slate-700">
                        Mobile Number *
                      </Label>
                      <Input
                        id="delivery_mobile"
                        data-testid="delivery-mobile-input"
                        type="tel"
                        value={formData.mobile_number}
                        onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                        placeholder="9876543210"
                        required
                        minLength={10}
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="delivery_vehicle_category" className="text-sm font-medium text-slate-700">
                        Vehicle Category *
                      </Label>
                      <Select
                        value={formData.vehicle_category}
                        onValueChange={(value) => setFormData({ ...formData, vehicle_category: value })}
                      >
                        <SelectTrigger data-testid="delivery-vehicle-category-select" className="mt-1.5">
                          <SelectValue placeholder="Select vehicle" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleCategories.map((category) => (
                            <SelectItem key={category} value={category}>{category}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="delivery_vehicle_number" className="text-sm font-medium text-slate-700">
                        Vehicle Number *
                      </Label>
                      <Input
                        id="delivery_vehicle_number"
                        data-testid="delivery-vehicle-number-input"
                        value={formData.vehicle_number}
                        onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
                        placeholder="TN 01 AB 1234"
                        required
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="delivery_city" className="text-sm font-medium text-slate-700">
                      City *
                    </Label>
                    <Input
                      id="delivery_city"
                      data-testid="delivery-city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Chennai"
                      required
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="delivery_message" className="text-sm font-medium text-slate-700">
                      Message
                    </Label>
                    <Textarea
                      id="delivery_message"
                      data-testid="delivery-message-input"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Any details about your vehicle, routes, or timing."
                      rows={5}
                      className="mt-1.5 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="delivery-submit-btn"
                    disabled={
                      isLoading ||
                      !formData.name ||
                      !formData.mobile_number ||
                      !formData.vehicle_category ||
                      !formData.vehicle_number ||
                      !formData.city
                    }
                    className="w-full py-3 bg-[#0055FF] text-white font-semibold hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 rounded-lg"
                  >
                    {isLoading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={20} weight="bold" />
                        Submit Onboarding Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
